import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useProjectStore } from '../../store/projectStore';
import SafeIcon from '../../common/SafeIcon';
import ShareModal from './ShareModal';
import DriveIntegrationModal from '../DriveIntegrationModal';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowLeft, 
  FiShare2, 
  FiEdit, 
  FiTrash2, 
  FiUsers, 
  FiClock, 
  FiVideo,
  FiDownload,
  FiCloud,
  FiCalendar,
  FiEye
} = FiIcons;

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getProjectById, prospects } = useProjectStore();
  
  const [project, setProject] = useState(null);
  const [shareModal, setShareModal] = useState({ isOpen: false, project: null });
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    const projectData = getProjectById(id);
    if (!projectData) {
      toast.error('Proyecto no encontrado');
      navigate('/dashboard/projects');
      return;
    }
    setProject(projectData);
  }, [id, getProjectById, navigate]);

  const downloadVideo = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShare = () => {
    setShareModal({ isOpen: true, project });
  };

  const closeShareModal = () => {
    setShareModal({ isOpen: false, project: null });
  };

  const getProjectProspects = () => {
    return prospects.filter(p => p.projectId === project?.id);
  };

  const calculateTimeLeft = (createdAt) => {
    const created = new Date(createdAt);
    const expiryDate = new Date(created.getTime() + (15 * 24 * 60 * 60 * 1000));
    const now = new Date();
    const diff = expiryDate - now;
    
    if (diff <= 0) return 'Expirado';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    return `${days}d ${hours}h`;
  };

  const VideoResponse = ({ video, index, projectId }) => {
    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(video.createdAt));
    const [showDriveModal, setShowDriveModal] = useState(false);

    useEffect(() => {
      const timer = setInterval(() => {
        setTimeLeft(calculateTimeLeft(video.createdAt));
      }, 1000);
      return () => clearInterval(timer);
    }, [video.createdAt]);

    return (
      <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
        <div className="relative">
          <video
            src={video.url}
            controls
            className="w-full rounded-lg"
          />
          <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
            {`Pregunta ${index + 1}`}
          </div>
        </div>
        
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-500">
              ID: VIXEA-{projectId}-{index}
            </span>
            <span className={`text-sm ${timeLeft === 'Expirado' ? 'text-red-500' : 'text-orange-500'}`}>
              Expira en: {timeLeft}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowDriveModal(true)}
              className="flex items-center space-x-1 px-3 py-1 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
            >
              <SafeIcon icon={FiCloud} className="w-4 h-4" />
              <span>Drive</span>
            </button>
            
            <button
              onClick={() => downloadVideo(video.url, `VIXEA-${projectId}-${index}.webm`)}
              className="flex items-center space-x-1 px-3 py-1 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <SafeIcon icon={FiDownload} className="w-4 h-4" />
              <span>Descargar</span>
            </button>
          </div>
        </div>
        
        {showDriveModal && (
          <DriveIntegrationModal
            video={video}
            projectId={projectId}
            questionIndex={index}
            onClose={() => setShowDriveModal(false)}
          />
        )}
      </div>
    );
  };

  if (!project) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-2 text-gray-600">Cargando proyecto...</span>
        </div>
      </div>
    );
  }

  const projectProspects = getProjectProspects();

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => navigate('/dashboard/projects')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          <span>Volver a Proyectos</span>
        </button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{project.title}</h1>
            <p className="text-gray-600">{project.company}</p>
          </div>
          
          <div className="flex items-center space-x-4">
            <button
              onClick={handleShare}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiShare2} className="w-4 h-4" />
              <span>Compartir</span>
            </button>
            
            <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2">
              <SafeIcon icon={FiEdit} className="w-4 h-4" />
              <span>Editar</span>
            </button>
          </div>
        </div>

        {/* Project Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiUsers} className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{projectProspects.length}</p>
                <p className="text-sm text-gray-600">Candidatos</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiVideo} className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{project.questions?.length || 0}</p>
                <p className="text-sm text-gray-600">Preguntas</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiClock} className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{project.responseTime}s</p>
                <p className="text-sm text-gray-600">Por respuesta</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiCalendar} className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {new Date(project.createdAt).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-600">Creado</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Project Details */}
      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-4">Información del Proyecto</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">ID del Proyecto</h3>
              <p className="text-gray-900">{project.vixeaId}</p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Descripción</h3>
              <p className="text-gray-900">{project.description || 'Sin descripción'}</p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Requisitos</h3>
              <p className="text-gray-900">{project.requirements || 'Sin requisitos específicos'}</p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Configuración</h3>
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  • Tiempo para pensar: {project.thinkingTime}s
                </p>
                <p className="text-sm text-gray-600">
                  • Tiempo de respuesta: {project.responseTime}s
                </p>
                <p className="text-sm text-gray-600">
                  • Tipo de respuesta: {project.responseType}
                </p>
                <p className="text-sm text-gray-600">
                  • CV requerido: {project.requireCV ? 'Sí' : 'No'}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-4">Preguntas</h2>
          
          {project.questions && project.questions.length > 0 ? (
            <div className="space-y-4">
              {project.questions.map((question, index) => (
                <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="bg-primary text-white text-xs font-medium px-2 py-0.5 rounded-full mr-2">
                          {index + 1}
                        </span>
                        <span className="text-sm text-gray-500">
                          {question.type === 'video' ? 'Video' : 'Texto'}
                        </span>
                      </div>
                      <p className="text-gray-900 mb-2">{question.text}</p>
                      <p className="text-xs text-gray-500">
                        Tiempo: {question.responseTime}s
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No hay preguntas configuradas</p>
          )}
        </motion.div>
      </div>

      {/* Responses Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Respuestas de Candidatos</h2>
          <button
            onClick={() => navigate('/dashboard/prospects')}
            className="text-primary hover:text-blue-700 text-sm font-medium flex items-center space-x-1"
          >
            <SafeIcon icon={FiEye} className="w-4 h-4" />
            <span>Ver todos los candidatos</span>
          </button>
        </div>

        {projectProspects.length > 0 ? (
          <div className="space-y-6">
            {projectProspects.map((prospect) => (
              <div key={prospect.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {prospect.name} {prospect.lastName}
                    </h3>
                    <p className="text-sm text-gray-600">{prospect.email}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(prospect.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium text-gray-900">
                      Puntuación IA: {prospect.aiScore}/5
                    </span>
                  </div>
                </div>

                {prospect.responses && prospect.responses.length > 0 && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">Videos de Respuesta:</h4>
                    {prospect.responses.map((response, index) => (
                      <VideoResponse
                        key={index}
                        video={{
                          url: response.response,
                          createdAt: prospect.createdAt
                        }}
                        index={index}
                        projectId={project.vixeaId}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <SafeIcon icon={FiUsers} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Aún no hay candidatos para este proyecto</p>
            <button
              onClick={handleShare}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiShare2} className="w-4 h-4" />
              <span>Compartir proyecto</span>
            </button>
          </div>
        )}
      </motion.div>

      {/* Share Modal */}
      <ShareModal
        project={shareModal.project}
        isOpen={shareModal.isOpen}
        onClose={closeShareModal}
      />
    </div>
  );
};

export default ProjectDetail;